// Package Imports
import {
  queryOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'

// Module Imports
import {
  createCustomGameMode,
  deleteCustomGameMode,
  getCustomGameMode,
  listAllCustomGameModes,
  listCustomGameModes,
  updateCustomGameMode,
} from '@/modules/custom-game-mode/custom-game-mode.service'
import type {
  TCreateCustomGameModeRequest,
  TCustomGameMode,
  TUpdateCustomGameModeRequest,
} from '@/modules/custom-game-mode/custom-game-mode.types'
import type { TAdminListParams } from '@/core/lib/table-query'

export const customGameModeListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['custom-game-modes', 'list', params],
    queryFn: () => listCustomGameModes(params),
    staleTime: 1000 * 30,
  })

export const customGameModeDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['custom-game-modes', 'detail', id],
    queryFn: () => getCustomGameMode(id),
    staleTime: 1000 * 30,
  })

export const allCustomGameModesQueryOptions = () =>
  queryOptions({
    queryKey: ['custom-game-modes', 'all'],
    queryFn: listAllCustomGameModes,
    staleTime: 1000 * 60,
  })

export const useCreateCustomGameMode = (
  options?: UseMutationOptions<
    TCustomGameMode,
    Error,
    TCreateCustomGameModeRequest
  >
) => useMutation({ mutationFn: createCustomGameMode, ...(options ?? {}) })

export const useUpdateCustomGameMode = (
  options?: UseMutationOptions<
    TCustomGameMode,
    Error,
    { id: string; body: TUpdateCustomGameModeRequest }
  >
) =>
  useMutation({
    mutationFn: ({ id, body }) => updateCustomGameMode(id, body),
    ...(options ?? {}),
  })

export const useDeleteCustomGameMode = (
  options?: UseMutationOptions<void, Error, string>
) => useMutation({ mutationFn: deleteCustomGameMode, ...(options ?? {}) })
